<template>
  <div
    ref="navItemRef"
    :class="[
      'ecx-nav-item group relative min-w-0',
      expanded ? 'w-full' : 'flex w-full justify-center',
    ]"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <a
      v-if="disabled"
      href="javascript:void(0)"
      :class="[baseClasses, disabledClasses]"
      @click="handleDisabledClick"
      :aria-disabled="true"
    >
      <span class="nav-icon inline-flex size-4 shrink-0 items-center justify-center">
        <slot :is-active="false" />
      </span>
      <Transition name="slide-fade">
        <span v-if="expanded" class="nav-label min-w-0 flex-1 truncate">
          {{ label }}
        </span>
      </Transition>
    </a>

    <RouterLink
      v-else
      :to="computedTo"
      v-slot="{ isActive, href, navigate }"
      custom
    >
      <a
        :href="href"
        :class="[baseClasses, effectiveActive(isActive) ? activeClasses : inactiveClasses]"
        :aria-current="effectiveActive(isActive) ? 'page' : undefined"
        @click="navigate"
      >
        <span class="nav-icon inline-flex size-4 shrink-0 items-center justify-center">
          <slot :is-active="effectiveActive(isActive)" />
        </span>
        <Transition name="slide-fade">
          <span v-if="expanded" class="nav-label min-w-0 flex-1 truncate">
            {{ label }}
          </span>
        </Transition>
      </a>
    </RouterLink>

    <!-- Tooltip (teleported to body to avoid clipping) -->
    <Teleport to="body">
      <Transition name="tooltip">
        <div
          v-if="!expanded && label && isHovered"
          class="ecx-nav-tooltip"
          :style="tooltipStyle"
          role="tooltip"
        >
          {{ label }}
          <div class="tooltip-arrow" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type CSSProperties } from 'vue'
import { useRoute, RouterLink, type RouteLocationRaw } from 'vue-router'

export interface NavItemProps {
  /** Rota de destino (string ou objeto de rota) */
  to: string | RouteLocationRaw
  /** Texto do item de navegação */
  label?: string
  /** Query params adicionais */
  query?: Record<string, string | number>
  /** Se o item está desabilitado */
  disabled?: boolean
  /** Se a sidebar está expandida */
  expanded?: boolean
  /** Mensagem ao clicar quando desabilitado */
  disabledMessage?: string
  /** Quando true, considera ativo se a rota atual começa com o path do link (para subrotas) */
  activeOnPathPrefix?: boolean
  /**
   * Com `activeOnPathPrefix`, paths que não devem ativar este item (ex.: irmãos `/cartoes/modelos`
   * quando o link é `/cartoes`).
   */
  activePathPrefixExclusions?: string[]
}

const props = withDefaults(defineProps<NavItemProps>(), {
  expanded: true,
  disabled: false,
})

const emit = defineEmits<{
  (e: 'disabled-click', message?: string): void
}>()

// Refs
const navItemRef = ref<HTMLElement | null>(null)
const isHovered = ref(false)
const tooltipPosition = ref({ top: 0, left: 0 })

const route = useRoute()

/** Ativo quando RouterLink diz que está ativo ou, se activeOnPathPrefix, quando path atual começa com o path do link. */
function effectiveActive(isActive: boolean): boolean {
  if (props.activeOnPathPrefix && typeof props.to === 'string') {
    const path = props.to.replace(/\?.*$/, '')
    const exclusions = props.activePathPrefixExclusions ?? []
    for (const exclusion of exclusions) {
      const normalized = exclusion.replace(/\?.*$/, '')
      if (route.path === normalized || route.path.startsWith(`${normalized}/`)) {
        return false
      }
    }
    return route.path === path || route.path.startsWith(`${path}/`)
  }
  return isActive
}

// Computed route
const computedTo = computed<RouteLocationRaw>(() => {
  if (typeof props.to === 'string') {
    return { path: props.to, query: props.query }
  }
  return { ...props.to, query: { ...(props.to as any).query, ...props.query } }
})

// Tooltip style (positioned relative to viewport)
const tooltipStyle = computed<CSSProperties>(() => ({
  position: 'fixed',
  top: `${tooltipPosition.value.top}px`,
  left: `${tooltipPosition.value.left}px`,
  zIndex: 9999,
}))

// Classes - matching original EcxNavButton styles
const baseClasses = computed(() => [
  'flex items-center gap-x-4 rounded-lg text-sm font-medium transition-colors duration-200 self-stretch',
  props.expanded
    ? 'w-full min-w-0 justify-start px-3 py-3'
    : 'w-14 h-14 shrink-0 justify-center',
])

const activeClasses = 'bg-royal-25 text-royal-500'
const inactiveClasses = 'bg-white text-subtitle-gray hover:bg-border-gray'
const disabledClasses = 'opacity-10 cursor-not-allowed text-white bg-primary'

// Handlers
const handleDisabledClick = () => {
  emit('disabled-click', props.disabledMessage)
}

const handleMouseEnter = () => {
  if (!props.expanded && navItemRef.value) {
    const rect = navItemRef.value.getBoundingClientRect()
    tooltipPosition.value = {
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    }
    isHovered.value = true
  }
}

const handleMouseLeave = () => {
  isHovered.value = false
}
</script>

<style scoped>
/* Slide fade transition for label */
.slide-fade-enter-active {
  transition: all 0.15s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.1s ease-in;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}
</style>

<!-- Tooltip styles - unscoped because it's teleported to body -->
<style>
.ecx-nav-tooltip {
  padding: 8px 12px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  font-size: 14px;
  color: #1f2937;
  white-space: nowrap;
  pointer-events: none;
  transform: translateY(-50%);
}

.ecx-nav-tooltip .tooltip-arrow {
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 6px solid white;
  filter: drop-shadow(-2px 0 1px rgba(0, 0, 0, 0.05));
}

/* Tooltip transition */
.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
  transform: translateY(-50%) translateX(-4px);
}
</style>

